import apiClient from "../api/apiClient";

const FIXED_RECEIVER_ID = 1;

/**
 * Validates and converts value to integer or returns null
 */
const toValidInt = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) ? parsed : null;
};

/**
 * Extracts messages array from various response formats
 */
const extractMessages = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.messages)) return payload.messages;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.info)) return payload.info;
  if (payload.info && Array.isArray(payload.info.messages)) return payload.info.messages;
  return [];
};

/**
 * Send a message (Text or Image)
 * Matches backend ChatController.php exactly
 */
export const sendMessage = async (payload) => {
  try {
    const senderId = toValidInt(payload.sender_id);
    if (senderId === null) {
      return { status: false, message: "Invalid sender id" };
    }

    const isImage = !!payload.image;
    const data = new FormData();

    // Fields exactly as validated in ChatController.php sendMessage
    data.append("sender_id", String(senderId));
    data.append("receiver_id", String(FIXED_RECEIVER_ID));
    data.append("message", payload.message || "");
    data.append("type", isImage ? "image" : "text");

    if (isImage) {
      // Must be 'image' field for backend $request->file('image')
      data.append("image", payload.image, payload.image.name);
    }

    const response = await apiClient.post("chat/send", data);
    return response.data;
  } catch (error) {
    console.error("Error sending message:", error?.response?.data || error);
    return { 
      status: false, 
      message: error?.response?.data?.message || "Failed to send message" 
    };
  }
};

/**
 * Get new messages (Polling)
 * Matches backend ChatController.php getMessages
 */
export const getNewMessages = async (userId, lastMessageId) => {
  try {
    const parsedUserId = toValidInt(userId);
    if (parsedUserId === null) return { status: false, messages: [] };

    const response = await apiClient.get(`chat/messages`, {
      params: {
        receiver_id: parsedUserId,
        last_message_id: toValidInt(lastMessageId) || 0,
      },
    });
    const payload = response.data || {};
    return {
      ...payload,
      status: payload.status !== false,
      info: extractMessages(payload),
    };
  } catch (error) {
    console.error("Error fetching new messages:", error?.response?.data || error);
    return { status: false, info: [] };
  }
};

/**
 * Get full chat history
 * Matches backend ChatController.php chatHistory
 */
export const getChatHistory = async (userId) => {
  try {
    const parsedUserId = toValidInt(userId);
    if (parsedUserId === null) return { status: false, info: [] };

    const response = await apiClient.get(`chat/history`, {
      params: {
        user_id: parsedUserId,
        receiver_id: FIXED_RECEIVER_ID,
      },
    });
    const payload = response.data || {};
    return {
      status: payload.status !== false,
      info: extractMessages(payload),
    };
  } catch (error) {
    console.error("Error fetching chat history:", error?.response?.data || error);
    return { status: false, info: [] };
  }
};
